import React, { useContext } from "react";
import { Link } from "react-router";
import styled, { keyframes } from "styled-components";
import { AuthContext } from "../auth/authProvider";
import assets from "../common/assets";
import colors from "../common/colors";
import mixins from "../common/mixins";
import { dimension, fontSize, space } from "../common/variables";
import Container from "../components/Container";
import { AuthenticatedUserContext } from "../contexts/AuthenticatedUserContext";
import Button from "./Button";
import DropdownMenu from "./DropdownMenu";

interface HeaderProps {
    blur?: boolean;
    municipalityName?: string;
    position: "fixed" | "sticky";
}

const Header = (props: React.PropsWithChildren<HeaderProps>) => {
    const { signout } = useContext(AuthContext);
    const { authUserData } = useContext(AuthenticatedUserContext);

    return (
        <Wrapper
            $blur={props.blur ? props.blur : undefined}
            $position={props.position}>
            <Container>
                <Content>
                    <Left>
                        <Link to="/">
                            <Logo src={assets.logo} />
                        </Link>
                    </Left>
                    <Center>
                        {props.municipalityName && (
                            <Title>{props.municipalityName}</Title>
                        )}
                    </Center>
                    <Right>
                        <DropdownMenu
                            headerContent={
                                authUserData && (
                                    <Menu>
                                        <Icon>{assets.user}</Icon>
                                        <MenuTitle>
                                            {authUserData.firstName?.length && authUserData.firstName !== 'unnamed'
                                                ? authUserData.firstName
                                                : "Mitt konto"}
                                        </MenuTitle>
                                    </Menu>
                                )
                            }>
                            <Button
                                size="small"
                                theme="inverted"
                                onClick={() => signout()}
                                title="Logga ut"
                            />
                        </DropdownMenu>
                    </Right>
                </Content>
                {props.children}
            </Container>
        </Wrapper>
    );
};

export default Header;

const fadeIn = keyframes`
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
`;

const Wrapper = styled.header<{
    $blur?: boolean;
    $position: "fixed" | "sticky";
}>`
    display: flex;
    flex-direction: column;
    background-color: ${colors.white};
    position: ${({ $position }) =>
        $position === "sticky" ? "sticky" : "fixed"};
    margin-bottom: ${({ $position }) => $position === "sticky" && "28px"};
    top: 0;
    left: 0;
    width: 100%;
    border-bottom: 1px solid ${colors.border};
    z-index: 9;
    transition: filter ${mixins.transitions.large};
    filter: ${({ $blur }) => ($blur ? "blur(4px)" : "none")};
`;

const Content = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
    position: relative;
    height: ${dimension.headerHeight};
    background-color: ${colors.white};
    z-index: 1;
`;

const Left = styled.div`
    display: flex;
    align-items: center;
    min-width: 0;
`;

const Center = styled.div`
    display: flex;
    justify-content: center;
    min-width: 0;
`;

const Right = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    min-width: 0;
`;

const Logo = styled.img`
    height: 40px;
`;

const Title = styled.h1`
    font-size: ${fontSize.large};
    margin: 0;
    display: block;
    max-width: 100%;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    animation: ${fadeIn} ${mixins.transitions.large} forwards;
`;

const Menu = styled.div`
    display: flex;
    animation: ${fadeIn} ${mixins.transitions.large} forwards;
    align-items: center;
`;

const MenuTitle = styled.span`
    margin-left: ${space.xSmall};
    font-size: ${fontSize.small};
`;

const Icon = styled.span`
    display: inline-block;
    fill: ${colors.black};
    height: 20px;
    width: 20px;
`;
